import { beginBattle } from "./dispatch";
import { cloneBattleState, Operation, Round, Situation, Side, RoundStep } from "./round";
import { Seer } from "./seer";
import { Pet, BattleStatus } from "./pet";
import type { BattleEvent } from "./events";

/**
 * 战斗阶段
 * - draft: 对战开始前的精灵池 ban & pick
 * - selecting: 等待双方选择操作
 * - executing: 回合动画回放中
 * - ended: 对战结束
 */
export type Phase = "draft" | "selecting" | "executing" | "ended";

/** 每方在 ban 阶段各需 ban 掉的精灵数量 */
export const BAN_PER_SIDE = 1;
/** 首轮 pick 数量（挑战方先手），后续每轮按此数量交替 */
const FIRST_PICK_COUNT = 1;
/**
 * PICK_COUNT，供本模块相关规则使用。
 */
const PICK_COUNT = 2;

/**
 * 视图模式
 * - live: 实时对局
 * - review: 回看历史
 */
export type ViewMode = "live" | "review";

/**
 * Battle：封装对战流程、操作选择与历史回看。
 */
export class Battle {
    /** 初始局面 */
    beginning: Situation;
    /** 实时对局当前局面（活动分支末端） */
    current: Situation;
    /** 历史回合记录 */
    rounds: Round[] = [];

    /** 双方待提交操作；undefined 表示尚未提交，skip 表示空过 */
    pendingLeft: Operation | undefined;
    /**
     * pendingRight，保存当前实例的pendingRight状态。
     */
    pendingRight: Operation | undefined;
    /**
     * openingEvents，保存当前实例的openingEvents状态。
     */
    openingEvents: BattleEvent[] = [];
    /**
     * 读取当前leftSelected。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get leftSelected() {
        return this.pendingLeft !== undefined;
    }
    /**
     * 读取当前rightSelected。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get rightSelected() {
        return this.pendingRight !== undefined;
    }

    /** 认输方与胜方；双方全灭时胜方为 null */
    surrenderedSide: Side | null = null;
    /**
     * winnerSide，保存当前实例的winnerSide状态。
     */
    winnerSide: Side | null = null;

    /** 双方待选补位精灵（上回合出战精灵阵亡时，需先选择替补登场再选操作） */
    pendingReplacementLeft: number | undefined = undefined;
    /**
     * pendingReplacementRight，保存当前实例的pendingReplacementRight状态。
     */
    pendingReplacementRight: number | undefined = undefined;

    /** ===== 精灵池 ban & pick（对战开始前）===== */
    /** 精灵池中待 ban / 待 pick 的精灵 */
    draftPool: Pet[] = [];
    /** 已被 ban 掉的精灵（双方均不可 pick） */
    draftBanned: Pet[] = [];
    /** 挑战方已 pick 的精灵 */
    draftLeft: Pet[] = [];
    /** 被挑战方已 pick 的精灵 */
    draftRight: Pet[] = [];
    /** 草稿阶段：ban / pick */
    draftStage: "ban" | "pick" = "ban";
    /** 当前操作方 */
    draftDrafter: Side = "left";
    /** 当前 pick 轮次剩余可连选数量 */
    draftRemainPick: number = 0;

    /** 战斗阶段 */
    phase: Phase = "draft";
    /** 当前执行的回合索引 */
    currentRoundIndex: number = -1;
    /** 当前已提交显示的步骤索引（-1=回合初始局面） */
    currentStep: number = -1;

    /** 视图模式 */
    viewMode: ViewMode = "live";
    /** 回看回合索引 */
    viewRound: number = 0;
    /** 回看步骤索引（-1=该回合初始局面） */
    viewStep: number = -1;

    /**
     * 创建实例并初始化所需的领域状态。
     * @param leftSeer 左方赛尔
     * @param rightSeer 右方赛尔
     * @param initialDraftPool initialDraftPool参数，参与本次操作
     * @param seed 随机数初始种子
     */
    constructor(leftSeer: Seer, rightSeer: Seer, initialDraftPool?: Pet[], seed?: number) {
        this.beginning = new Situation(leftSeer, rightSeer, seed);
        this.current = cloneBattleState(this.beginning);

        /**
         * packagePets，供本模块相关规则使用。
         */
        const packagePets = [
            ...this.beginning.leftSeer.petPackage,
            ...this.beginning.rightSeer.petPackage,
        ];
        this.draftPool = initialDraftPool ? cloneBattleState(initialDraftPool) : packagePets;
        this.draftPool.forEach((pet) => (pet.战斗状态 = BattleStatus.STANDBY));

        // ban & pick 完成前，候选精灵只属于精灵池。
        this.beginning.leftSeer.petPackage = [];
        this.beginning.rightSeer.petPackage = [];
    }

    /**
     * 玩家选择本回合操作
     * 双方均提交后自动开始执行回合
     */
    selectOperation(side: Side, op: Operation) {
        if (this.phase !== "selecting" || this.viewMode !== "live") {
            return;
        }
        if (op.kind === "surrender") {
            this.surrender(side);
            return;
        }
        if (!this.isOperationValid(side, op)) return;
        // 需要补位的一方必须先选择替补精灵登场，再选择本回合操作
        if (this.needsReplacement(side) && !this.getReplacement(side)) {
            console.debug(`${side === "left" ? "挑战方" : "被挑战方"} 需先选择补位精灵登场`);
            return;
        }
        if (side === "left") {
            this.pendingLeft = cloneBattleState(op);
        } else {
            this.pendingRight = cloneBattleState(op);
        }
        console.debug(
            `${side === "left" ? "挑战方" : "被挑战方"} 选择操作: ${this.describeOp(side, op)}`,
        );
        this.tryExecuteRound();
    }

    /**
     * 取消某方已选择的操作
     */
    cancelOperation(side: Side) {
        if (this.phase !== "selecting" || this.viewMode !== "live") return;
        if (side === "left") {
            this.pendingLeft = undefined;
        } else {
            this.pendingRight = undefined;
        }
    }

    /** 认输立即结束对战，不生成回合记录。 */
    surrender(side: Side) {
        if (this.phase !== "selecting" || this.viewMode !== "live") return;
        this.surrenderedSide = side;
        this.winnerSide = side === "left" ? "right" : "left";
        this.pendingLeft = undefined;
        this.pendingRight = undefined;

        this.pendingReplacementLeft = undefined;
        this.pendingReplacementRight = undefined;
        this.phase = "ended";
        console.info(
            `${side === "left" ? "挑战方" : "被挑战方"}认输，${
                this.winnerSide === "left" ? "挑战方" : "被挑战方"
            }获胜`,
        );
    }

    /**
     * 某方是否需要先补位（背包有存活精灵但无出战中的精灵）
     * 由当前局面直接推导，分叉/回看截断后自动一致
     */
    needsReplacement(side: Side): boolean {
        if (this.checkOver()) return false;
        /**
         * seer，供本模块相关规则使用。
         */
        const seer = side === "left" ? this.current.leftSeer : this.current.rightSeer;
        /**
         * hasFighting，供本模块相关规则使用。
         */
        const hasFighting = seer.petPackage.some((p) => p.战斗状态 === BattleStatus.FIGHTING);
        /**
         * hasAlive，供本模块相关规则使用。
         */
        const hasAlive = seer.petPackage.some((p) => p.isAlive());
        return !hasFighting && hasAlive;
    }

    /**
     * 阵亡方选择补位精灵（先登场，再进入正常操作选择）
     * 重复选择可改选
     */
    selectReplacement(side: Side, petId: number) {
        /**
         * seer，供本模块相关规则使用。
         */
        const seer = side === "left" ? this.current.leftSeer : this.current.rightSeer;
        /**
         * pet，供本模块相关规则使用。
         */
        const pet = seer.petPackage.find((candidate) => candidate.序号 === petId);
        if (!pet) return;
        if (this.phase !== "selecting" || this.viewMode !== "live") {
            return;
        }
        if (!this.needsReplacement(side)) return;
        // 仅允许选择背包中存活的待命精灵
        if (pet.战斗状态 !== BattleStatus.STANDBY || !pet.isAlive()) return;
        /**
         * previousReplacement，供本模块相关规则使用。
         */
        const previousReplacement = this.getReplacement(side);
        /**
         * replacementChanged，供本模块相关规则使用。
         */
        const replacementChanged = previousReplacement?.序号 !== pet.序号;
        if (side === "left") {
            this.pendingReplacementLeft = pet.序号;
        } else {
            this.pendingReplacementRight = pet.序号;
        }
        if (replacementChanged) {
            this.cancelOperation(side);
        }
        console.debug(`${side === "left" ? "挑战方" : "被挑战方"} 选择补位精灵: ${pet.名字}`);
        this.tryExecuteRound();
    }

    /**
     * 某方已选择的补位精灵
     */
    getReplacement(side: Side): Pet | undefined {
        /**
         * id，供本模块相关规则使用。
         */
        const id = side === "left" ? this.pendingReplacementLeft : this.pendingReplacementRight;
        /**
         * seer，供本模块相关规则使用。
         */
        const seer = side === "left" ? this.current.leftSeer : this.current.rightSeer;
        return seer.petPackage.find((pet) => pet.序号 === id);
    }

    /** ===== 精灵池 ban & pick ===== */

    /**
     * 当前操作方在精灵池中选择一只精灵（ban 或 pick）
     * @param side 操作方（必须等于当前操作方 draftDrafter）
     * @param pet 精灵池中的一只精灵
     */
    draftSelect(side: Side, pet: Pet) {
        if (this.phase !== "draft" || this.viewMode !== "live") {
            return;
        }
        if (side !== this.draftDrafter) {
            console.debug(
                `当前为${this.sideText(this.draftDrafter)}操作，${this.sideText(side)}无法选择`,
            );
            return;
        }
        if (!this.draftPool.includes(pet)) {
            console.debug(`【${pet.名字}】不在精灵池中`);
            return;
        }
        this.draftPool = this.draftPool.filter((p) => p !== pet);

        if (this.draftStage === "ban") {
            this.draftBanned.push(pet);
            console.info(`【${this.sideText(side)}】ban 掉【${pet.名字}】`);
        } else {
            (side === "left" ? this.draftLeft : this.draftRight).push(pet);
            this.draftRemainPick -= 1;
            console.info(
                `【${this.sideText(side)}】pick 了【${pet.名字}】${
                    this.draftRemainPick > 0 ? `（本轮还可选 ${this.draftRemainPick} 只）` : ""
                }`,
            );
        }
        this.advanceDraftTurn();
    }

    /**
     * 推进草稿回合
     * - ban 阶段：交替左右，双方各 ban BAN_PER_SIDE 只后进入 pick 阶段（挑战方先手，首轮 1 只）
     * - pick 阶段：当前方选完本轮的连选数量后换边，每轮连选数量为 2（末轮按剩余池数截断）
     * - 池空后提交最终阵容并进入选择阶段
     */
    private advanceDraftTurn() {
        if (this.draftPool.length === 0) {
            this.commitDraft();
            return;
        }
        if (this.draftStage === "ban") {
            if (this.draftBanned.length >= BAN_PER_SIDE * 2) {
                this.draftStage = "pick";
                this.draftDrafter = "left";
                this.draftRemainPick = Math.min(FIRST_PICK_COUNT, this.draftPool.length);
            } else {
                this.draftDrafter = this.draftDrafter === "left" ? "right" : "left";
            }
            return;
        }
        if (this.draftRemainPick > 0) {
            return;
        }
        // 换边：双方交换 pick 顺序，每轮连选 2 只，末轮按剩余池数截断
        this.draftDrafter = this.draftDrafter === "left" ? "right" : "left";
        this.draftRemainPick = Math.min(PICK_COUNT, this.draftPool.length);
    }

    /**
     * 提交草稿结果：将双方 pick 的精灵写入初始局面背包
     * 各自首个 pick 的精灵作为首发出战，其余待命
     */
    private commitDraft() {
        this.applyDraftToSeer(this.beginning.leftSeer, this.draftLeft);
        this.applyDraftToSeer(this.beginning.rightSeer, this.draftRight);
        this.settleInitialPetOn();
        this.phase = "selecting";
        console.info(
            `草稿结束：挑战方 ${this.draftLeft.map((p) => p.名字).join("、")}；被挑战方 ${this.draftRight
                .map((p) => p.名字)
                .join("、")}`,
        );
    }

    /**
     * applyDraftToSeer：执行对应的领域操作。
     * @param seer 所属赛尔
     * @param picked picked参数，参与本次操作
     */
    private applyDraftToSeer(seer: Seer, picked: Pet[]) {
        seer.petPackage.forEach((p) => (p.战斗状态 = BattleStatus.STANDBY));
        seer.petPackage = picked;
        if (picked.length > 0) {
            picked[0].战斗状态 = BattleStatus.FIGHTING;
        }
    }

    /** 开局事件只执行一次，分叉复用已完成开局的 beginning 快照。 */
    private settleInitialPetOn() {
        beginBattle(this.beginning.leftSeer, this.beginning.rightSeer, this.openingEvents);
        this.current = cloneBattleState(this.beginning);
    }

    /**
     * sideText：执行对应的领域操作。
     * @param side 操作所属的一方
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private sideText(side: Side): string {
        return side === "left" ? "挑战方" : "被挑战方";
    }

    /**
     * 双方操作齐全且所有需补位方均已选替补时，执行回合
     */
    private tryExecuteRound() {
        if (!this.leftSelected || !this.rightSelected) {
            return;
        }
        if (this.needsReplacement("left") && this.pendingReplacementLeft === undefined) {
            return;
        }
        if (this.needsReplacement("right") && this.pendingReplacementRight === undefined) {
            return;
        }
        this.executeRound();
    }

    /**
     * 执行回合：计算并进入回放阶段
     */
    executeRound() {
        if (!this.pendingLeft || !this.pendingRight || this.phase !== "selecting") return;
        /**
         * round，供本模块相关规则使用。
         */
        const round = new Round(
            this.pendingLeft,
            this.pendingRight,
            this.pendingReplacementLeft,
            this.pendingReplacementRight,
        );
        round.compute(this.current);
        this.rounds.push(round);
        this.currentRoundIndex = this.rounds.length - 1;
        this.currentStep = -1;
        this.phase = "executing";
        console.info(
            `第 ${this.rounds.length} 回合开始：左[${round.describeOperation(
                "left",
            )}] / 右[${round.describeOperation("right")}]`,
        );
    }

    /**
     * 读取当前currentRound。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get currentRound(): Round | undefined {
        if (this.currentRoundIndex < 0) return undefined;
        return this.rounds[this.currentRoundIndex];
    }

    /**
     * 当前执行回合中正在显示的步骤
     */
    get currentRoundStep(): RoundStep | undefined {
        /**
         * round，供本模块相关规则使用。
         */
        const round = this.currentRound;
        if (!round || this.currentStep < 0) return undefined;
        return round.steps[this.currentStep];
    }

    /**
     * 推进一步：提交显示当前回合的下一步快照
     * 由 Seer.vue 在动画播放完成后调用
     */
    advanceStep() {
        if (this.phase !== "executing") return;
        this.currentStep++;
    }

    /**
     * 结束当前回合并切换到选择阶段
     */
    finishRound() {
        if (this.phase !== "executing") return;
        /**
         * round，供本模块相关规则使用。
         */
        const round = this.currentRound;
        if (round && round.steps.length > 0) {
            this.current = cloneBattleState(round.finalSnapshot);
        }
        this.currentStep = -1;
        this.pendingLeft = undefined;
        this.pendingRight = undefined;

        this.pendingReplacementLeft = undefined;
        this.pendingReplacementRight = undefined;
        this.phase = this.checkOver() ? "ended" : "selecting";
        this.updateWinner();
        if (this.phase === "ended") {
            console.info("对战结束");
        }
    }

    /**
     * 统一的显示局面出口
     * - review: 返回历史回合步骤快照
     * - live+executing: 返回当前执行步骤快照
     * - live+selecting/ended: 返回实时当前局面
     */
    get displaySituation(): Situation {
        if (this.viewMode === "review") {
            /**
             * round，供本模块相关规则使用。
             */
            const round = this.rounds[this.viewRound];
            if (!round) return this.current;
            if (this.viewStep < 0) return round.initialSnapshot;
            return round.steps[this.viewStep]?.snapshot ?? round.initialSnapshot;
        }
        if (this.phase === "executing") {
            /**
             * round，供本模块相关规则使用。
             */
            const round = this.currentRound;
            if (!round) return this.current;
            if (this.currentStep < 0) return round.initialSnapshot;
            return round.steps[this.currentStep]?.snapshot ?? this.current;
        }
        return this.current;
    }

    /**
     * 进入回看模式
     * @param roundIdx 回合索引
     * @param stepIdx 步骤索引（-1=该回合初始局面）
     */
    review(roundIdx: number, stepIdx: number = -1) {
        if (roundIdx < 0 || roundIdx >= this.rounds.length) return;
        this.viewRound = roundIdx;
        this.viewStep = stepIdx;
        this.viewMode = "review";
    }

    /**
     * 返回实时对局
     */
    resumeLive() {
        this.viewMode = "live";
    }

    /**
     * 从某一回合处分叉：丢弃该回合及之后的记录，重新选择操作
     * @param roundIdx 要重新出招的回合索引
     */
    branchFrom(roundIdx: number) {
        if (roundIdx < 0 || roundIdx > this.rounds.length) return;
        this.rounds = this.rounds.slice(0, roundIdx);
        if (roundIdx === 0) {
            this.current = cloneBattleState(this.beginning);
        } else {
            /**
             * prev，供本模块相关规则使用。
             */
            const prev = this.rounds[roundIdx - 1];
            this.current = cloneBattleState(prev.finalSnapshot);
        }
        this.currentRoundIndex = -1;
        this.currentStep = -1;
        this.pendingLeft = undefined;
        this.pendingRight = undefined;

        this.pendingReplacementLeft = undefined;
        this.pendingReplacementRight = undefined;
        this.surrenderedSide = null;
        this.winnerSide = null;
        this.viewMode = "live";
        this.phase = this.checkOver() ? "ended" : "selecting";
        this.updateWinner();
        console.info(`从第 ${roundIdx + 1} 回合处重新出招`);
    }

    /**
     * 某方是否已选择操作
     */
    hasSelected(side: Side): boolean {
        return side === "left" ? this.leftSelected : this.rightSelected;
    }

    /**
     * 某方待选操作
     */
    getPending(side: Side): Operation | undefined {
        return side === "left" ? this.pendingLeft : this.pendingRight;
    }

    /**
     * 对战是否结束（任一方全灭）
     */
    checkOver(): boolean {
        /**
         * leftAlive，供本模块相关规则使用。
         */
        const leftAlive = this.current.leftSeer.petPackage.some((p) => p.isAlive());
        /**
         * rightAlive，供本模块相关规则使用。
         */
        const rightAlive = this.current.rightSeer.petPackage.some((p) => p.isAlive());
        return !leftAlive || !rightAlive;
    }

    /**
     * updateWinner：执行对应的领域操作。
     */
    private updateWinner() {
        /**
         * left，供本模块相关规则使用。
         */
        const left = this.current.leftSeer.petPackage.some((pet) => pet.isAlive());
        /**
         * right，供本模块相关规则使用。
         */
        const right = this.current.rightSeer.petPackage.some((pet) => pet.isAlive());
        this.winnerSide = left === right ? null : left ? "left" : "right";
    }

    /**
     * isOperationValid：执行对应的领域操作。
     * @param side 操作所属的一方
     * @param operation operation参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private isOperationValid(side: Side, operation: Operation): boolean {
        /**
         * seer，供本模块相关规则使用。
         */
        const seer = side === "left" ? this.current.leftSeer : this.current.rightSeer;
        /**
         * pet，供本模块相关规则使用。
         */
        const pet = this.getReplacement(side) ?? seer.getActivePet();
        if (!pet || !pet.isAlive()) return false;
        if (operation.kind === "skill")
            return (
                operation.petId === pet.序号 &&
                Number.isInteger(operation.skillIndex) &&
                (pet.技能组[operation.skillIndex]?.curPP ?? 0) > 0
            );
        if (operation.kind === "switch")
            return seer.petPackage.some(
                (candidate) =>
                    candidate.序号 === operation.petId &&
                    candidate !== pet &&
                    candidate.战斗状态 === BattleStatus.STANDBY &&
                    candidate.isAlive(),
            );
        if (operation.kind === "prop")
            return seer.propPackage.some(
                (prop) => prop.type === operation.propType && prop.curUses > 0,
            );
        return operation.kind === "skip";
    }

    /**
     * describeOp：执行对应的领域操作。
     * @param side 操作所属的一方
     * @param operation operation参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    private describeOp(side: Side, operation: Operation): string {
        /**
         * seer，供本模块相关规则使用。
         */
        const seer = side === "left" ? this.current.leftSeer : this.current.rightSeer;
        if (operation.kind === "skill")
            return `技能·${seer.petPackage.find((pet) => pet.序号 === operation.petId)?.技能组[operation.skillIndex]?.name ?? "未知"}`;
        if (operation.kind === "switch")
            return `切换·${seer.petPackage.find((pet) => pet.序号 === operation.petId)?.名字 ?? "未知"}`;
        if (operation.kind === "prop") return `道具·${operation.propType}`;
        return operation.kind === "surrender" ? "认输" : "空过";
    }
}
